import useQuery from "./useQuery";
import useTeamID from "./useTeamID";

/**
 * Acceso del usuario logueado a las features del team: `GET /plans/team/:teamID/access`
 * (api/src/plans/featureAccess.js tiene el contrato). Es la fuente unica del front,
 * con el enforcement de planes prendido o apagado: el back ya resuelve plan, activacion
 * por team, especialidad y TyC. Regla para pintar:
 *
 *   visible: false     → no se muestra (otra especialidad, o no activada en el team)
 *   blockedBy: 'plan'  → candado + "Ver planes"
 *   blockedBy: 'terms' → se muestra, y al usarla se piden los TyC
 *   blockedBy: null    → se usa
 *
 * La ruta NO pasa por `window._l()` ni cuelga de `/teams/*`: la regla de ALB 487 manda
 * los GET de `/teams/*` a identity y el request no llegaria al monolito.
 *
 * Tres estados (decision de Fran, 2026-10-07):
 *   - cargando: no se muestra ninguna feature, para no mostrar y despues esconder.
 *   - error o sin respuesta: se muestra todo sin candado ni TyC. La barrera real es el
 *     api, que corta igual; el front no puede conceder nada.
 *   - listo: lo que diga el contrato.
 */

// Sin respuesta en este tiempo cuenta como error (se muestra todo), para no dejar
// la HCE sin sus herramientas si el api no contesta.
const TIMEOUT_MS = 5000;
const STALE_MS = 30000;

const LOADING = Object.freeze({ visible: false, usable: false, blockedBy: null, restriction: null, voice: false });
const OPEN = Object.freeze({ visible: true, usable: true, blockedBy: null, restriction: null, voice: true });

export default function useFeatureAccess() {
  const teamID = useTeamID().replace(/^teams\//, "");

  // Un 404/403 no se arregla reintentando (el default de react-query v3 son 3).
  const query = useQuery(teamID ? `plans/team/${teamID}/access` : "", {
    staleTime: STALE_MS,
    retry: false,
    timeout: TIMEOUT_MS,
  });

  const ready = !query.isError && !!query.data?.features;
  const loading = !ready && !query.isError && (query.isLoading || !teamID);
  const failed = !ready && !loading;

  const access = (key) => {
    if (loading) return LOADING;
    if (failed) return OPEN;
    // Una key que el api no conoce (front adelantado al api) no se bloquea.
    const feature = query.data.features[key];
    if (!feature) return OPEN;
    return {
      visible: feature.visible === true,
      usable: feature.usable === true,
      blockedBy: feature.blockedBy ?? null,
      restriction: feature.restriction ?? null,
      voice: feature.voice === true,
    };
  };

  return {
    loading,
    ready,
    failed,
    enforced: ready && query.data.enforced === true,
    // null o { value: null } = sin tope. Con el enforcement apagado llega sin tope.
    agendaCap: ready ? query.data.limits?.agendaCap ?? null : null,
    access,
  };
}
